import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import useThemeStyles from '../../../theme/useThemeStyles';

function Step2Verification({ formData, onComplete, onError }) {
  const styles = useThemeStyles();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  // Check if user is already verified
  useEffect(() => {
    const checkVerificationStatus = async () => {
      // Check multiple sources for verification status
      if (formData.verified || formData.user?.isEmailVerified) {
        setIsVerified(true);
        setChecking(false);
        return;
      }

      // If we have a token, check the user's verification status
      const token = localStorage.getItem('token');
      if (token && formData.userId) {
        try {
          api.defaults.headers.common['x-auth-token'] = token;
          const response = await api.get(`/api/supplier/user`);
          if (response.data?.user?.isEmailVerified) {
            setIsVerified(true);
          }
        } catch (err) {}
      }
      setChecking(false);
    };

    checkVerificationStatus();
  }, [formData]);

  const handleSkip = () => {
    // If already verified, just proceed
    onComplete({
      ...formData,
      verified: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      
      const response = await api.post('/api/supplier/verify-email', {
        userId: formData.userId,
        code: verificationCode
      });
      
      // Store authentication data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userId', response.data.user.id);
      localStorage.setItem('userType', 'supplier');
      api.defaults.headers.common['x-auth-token'] = response.data.token;
      
      // Set user in context
      if (setUser) {
        setUser(response.data.user);
      }
      
      onComplete({
        ...formData,
        verified: true,
        token: response.data.token,
        user: response.data.user
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Invalid verification code';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResending(true);
      setError('');
      
      await api.post('/api/supplier/resend-email-verification', {
        userId: formData.userId
      });
      
      setError('');
      // Show success message
      setTimeout(() => {
        setError('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend verification code');
    } finally {
      setResending(false);
    }
  };

  // If already verified, show success message and skip button
  if (checking) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2, ...styles.text.secondary }}>
          Checking verification status...
        </Typography>
      </Box>
    );
  }

  if (isVerified) {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
            Email Already Verified
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, ...styles.text.secondary }}>
            Your email has already been verified. You can proceed to the next step.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleSkip}
            sx={{ minWidth: 120 }}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
        Confirm Your Email
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
        We've sent you a magic code to keep your account secure. Please check your email for the verification code.
      </Typography>

      {error && (
        <Alert severity={error.includes('resend') ? 'info' : 'error'} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => {
            setVerificationCode(e.target.value);
            if (error) setError('');
          }}
          required
          variant="outlined"
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 6 }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Verify Email'}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            onClick={handleResend}
            disabled={resending}
            sx={{ cursor: resending ? 'not-allowed' : 'pointer' }}
          >
            {resending ? 'Resending...' : "Didn't receive the code? Resend"}
          </Link>
        </Box>
      </form>
    </Box>
  );
}

export default Step2Verification;

